<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '450px' }"
    :header="dialogHeader"
  >
    <div class="p-fluid">
      <p>{{ t('myLists.actions.deleteFromAllLists.confirmMessage') }}</p>
    </div>
    <template #footer>
      <SpmButton
        :label="t('common.close')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('validate')"
        icon="pi pi-check"
        class-style="p-button p-button-danger"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, DeleteFromAllListsResult } from './types';

export default defineComponent({
  name: 'DeleteFromAllListsAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const currentPayload = ref<ActionPayload | null>(null);

    // Computed header based on context
    const dialogHeader = computed(() => {
      if (currentPayload.value?.context === 'grouped') {
        return t('myLists.actions.deleteFromAllLists.headerGrouped');
      }
      return t('myLists.actions.deleteFromAllLists.headerRow');
    });

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: ActionPayload) => {
      currentPayload.value = payload;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = () => {
      if (!currentPayload.value) return;

      const result: DeleteFromAllListsResult = {
        context: currentPayload.value.context,
        selectionInfo: currentPayload.value.selectionInfo,
        rowId: currentPayload.value.rowId,
      };

      emit('confirm', result);
      close();
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      dialogHeader,
      close,
      confirm,
    };
  },
});
</script>
